import { Film, ImagePlus, Link2, X } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { MediaKind } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { MediaPickerDialog, MediaThumb, useMedia } from '@/features/media';

import { emptyMediaUrl } from '../api/mappers';
import { mediaKinds, type MediaRefForm } from '../schemas/block-form-schema';

import { fieldText, type LooseForm } from './field-text';

export interface MediaRefFieldProps {
  /** Path of the media value, e.g. `items.0.media` or `header.mobileMedia`. */
  name: string;
  label: string;
  description?: string;
}

function LibraryThumb({ mediaId }: { mediaId: string }) {
  const { data } = useMedia(mediaId);
  return <MediaThumb media={data} className="size-20 shrink-0 rounded-md border" />;
}

/**
 * Image or video for a banner item: a library pick (alt comes from the library unless
 * overridden) or an https URL that carries its own alt and size. Alt text is mandatory either
 * way because the storefront renders it and doc 15 requires it.
 */
export function MediaRefField({ name, label, description }: MediaRefFieldProps) {
  const { t } = useTranslation('content');
  const form = useFormContext<LooseForm>();
  const value = form.watch(name) as MediaRefForm;
  const [picker, setPicker] = useState<MediaKind | null>(null);
  const sub = (field: string) => `${name}.${field}`;
  const set = (next: MediaRefForm) =>
    form.setValue(name, next, { shouldDirty: true, shouldValidate: true });

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3" role="group" aria-label={label}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{label}</p>
          {description ? <p className="text-muted-foreground text-xs">{description}</p> : null}
        </div>
        {value ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => set(null)}>
            <X aria-hidden />
            {t('media.remove')}
          </Button>
        ) : null}
      </div>
      {value === null ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPicker('image')}>
            <ImagePlus aria-hidden />
            {t('media.chooseImage')}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setPicker('video')}>
            <Film aria-hidden />
            {t('media.chooseVideo')}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => set(emptyMediaUrl())}>
            <Link2 aria-hidden />
            {t('media.useUrl')}
          </Button>
        </div>
      ) : value.type === 'library' ? (
        <div className="flex items-start gap-4">
          <LibraryThumb mediaId={value.mediaId} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <FormField
              control={form.control}
              name={sub('alt')}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('media.altOverride')}</FormLabel>
                  <FormControl>
                    <Input {...field} value={fieldText(field.value)} />
                  </FormControl>
                  <FormDescription>{t('media.altOverrideHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => setPicker('image')}>
                {t('media.change')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <FormField
            control={form.control}
            name={sub('url')}
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>{t('media.url')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} placeholder="https://" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={sub('kind')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('media.kind')}</FormLabel>
                <Select value={fieldText(field.value)} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {mediaKinds.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {t(`media.kinds.${kind}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={sub('alt')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('media.alt')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={sub('width')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('media.width')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} inputMode="numeric" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={sub('height')}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('media.height')}</FormLabel>
                <FormControl>
                  <Input {...field} value={fieldText(field.value)} inputMode="numeric" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
      <MediaPickerDialog
        open={picker !== null}
        onOpenChange={(open) => {
          if (!open) setPicker(null);
        }}
        kind={picker ?? 'image'}
        multiple={false}
        onPick={(picked) => {
          const first = picked[0];
          if (first) set({ type: 'library', mediaId: first.id, alt: '' });
        }}
      />
    </div>
  );
}
